import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock3, FileSearch, Landmark, ShieldCheck } from 'lucide-react';
import { processService } from '../../services/api';
import ConsultaPublicaSearch from '../../components/Public/ConsultaPublicaSearch';
import PublicIssuedLicenses from '../../components/Public/PublicIssuedLicenses';
import PublicFaq from '../../components/Public/PublicFaq';
import PublicPageShell from '../../components/Public/PublicPageShell';
import PublicProcessResult from '../../components/Public/PublicProcessResult';
import PublicChannelClosed from '../../components/Public/PublicChannelClosed';
import { useTenant } from '../../contexts/TenantContext';
import { isPublicConsultaEnabled } from '../../utils/publicPages';

const ConsultaProcesso = () => {
  const { tenant } = useTenant();
  const [params, setParams] = useSearchParams();
  const initialQuery = params.get('numero') || '';
  const orgName = tenant?.name;

  if (!isPublicConsultaEnabled(tenant)) {
    return (
      <PublicPageShell
        eyebrow="Transparência do órgão"
        title="Consulta pública de andamento"
        lead="Este município desligou a consulta pública. Quem já tem cadastro continua acessando o andamento pelo login."
      >
        <PublicChannelClosed
          title="Consulta pública indisponível"
          text="O administrador deste órgão desativou a consulta de andamento sem login. Entre no sistema ou fale com o protocolo."
        />
      </PublicPageShell>
    );
  }

  return (
    <PublicPageShell
      eyebrow="Transparência do órgão"
      title="Consulta pública de andamento"
      lead="Acompanhe o protocolo deste município sem login, ou veja as licenças já emitidas. O número do processo é o gerado no useAmbiental — não é SEI, Diário Oficial nem sistema do IBAMA."
      aside={
        <>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-slate-200 ring-1 ring-white/5">
            <p className="flex items-center gap-2 font-semibold text-white">
              <FileSearch className="h-4 w-4 text-primary-300" aria-hidden />
              O que esta consulta mostra
            </p>
            <ul className="mt-3 space-y-2 text-slate-300">
              <li>Número, tipo e situação pública do processo.</li>
              <li>Data de abertura e tempo decorrido no órgão.</li>
              <li>Linha do tempo resumida (protocolado → análise → conclusão).</li>
              <li>Link compartilhável e comprovante para imprimir.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-slate-300">
            <p className="flex items-center gap-2 font-semibold text-white">
              <ShieldCheck className="h-4 w-4 text-primary-300" aria-hidden />
              O que não aparece
            </p>
            <p className="mt-2 leading-relaxed">
              Documentos internos, pareceres, dados de terceiros e o conteúdo da caixa do servidor ficam fora desta tela.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-primary-800/40 to-slate-900/40 p-5 text-sm">
            <p className="flex items-center gap-2 font-semibold text-white">
              <Landmark className="h-4 w-4 text-primary-200" aria-hidden />
              É requerente cadastrado?
            </p>
            <p className="mt-2 text-slate-300">
              Entre no portal para protocolar petições e ver exigências com o mesmo número.
            </p>
            <Link to="/login" className="mt-3 inline-flex text-sm font-semibold text-primary-200 hover:text-white">
              Acessar o sistema →
            </Link>
          </div>
          <PublicFaq
            items={[
              {
                q: 'Qual número usar?',
                a: 'O protocolo deste órgão no useAmbiental. Números de SEI, IBAMA, cartório ou outro município não retornam resultado aqui.',
              },
              {
                q: 'Por que não vejo o despacho?',
                a: 'A consulta pública informa só o andamento. Peças, pareceres e arquivos ficam no portal do requerente ou no atendimento do protocolo.',
              },
              {
                q: 'Posso compartilhar o resultado?',
                a: 'Sim. Copie o link da consulta — ele abre esta mesma página já com o número preenchido, sem exigir login.',
              },
            ]}
          />
        </>
      }
    >
      <ConsultaPublicaSearch
        placeholder="Número do processo"
        entityLabel="processo"
        idleHint="Digite o protocolo completo ou parte dele (mínimo 2 caracteres)."
        initialQuery={initialQuery}
        autoSearch={Boolean(initialQuery)}
        onSearched={(needle) => {
          const next = new URLSearchParams(params);
          if (needle) next.set('numero', needle);
          else next.delete('numero');
          setParams(next, { replace: true });
        }}
        search={async (numero) => {
          const { data } = await processService.consultarProcesso(numero);
          return data;
        }}
        renderItem={(row) => (
          <>
            <strong>{row.numero}</strong> · {row.situacao_label || row.situacao}
            {row.data_abertura ? ` · abertura ${String(row.data_abertura).slice(0, 10)}` : ''}
          </>
        )}
        renderResults={(items) => (
          <div className="space-y-4">
            {items.map((row, idx) => (
              <PublicProcessResult key={row.numero || idx} row={row} orgName={orgName} />
            ))}
          </div>
        )}
        extraEmpty={
          <p className="pt-2">
            Tem cadastro neste órgão?{' '}
            <Link to="/login" className="font-medium text-primary-700 underline">
              Entre no sistema
            </Link>{' '}
            para ver o portal do requerente.
          </p>
        }
      />
      <PublicIssuedLicenses />
      <p className="mt-6 flex items-center gap-2 text-xs text-gray-500">
        <Clock3 className="h-3.5 w-3.5" aria-hidden />
        A situação pública é atualizada conforme o órgão tramita o processo.
      </p>
    </PublicPageShell>
  );
};

export default ConsultaProcesso;
